import React, { useState } from 'react';
import { APPLICATION_STATUS_STYLE, applicationRef, companyOf, jobOf } from './api';

/* ==================================================================
   ApplicationCard
   ------------------------------------------------------------------
   One submitted application, drawn the same way everywhere it is
   shown - the Submitted Applications list on the dashboard and the
   receipt on the job page the candidate just applied from. Both used
   to have to agree by hand about what an application looks like, and
   neither showed its reference.
   ================================================================== */

function CopyableRef({ value }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* Clipboard is blocked in some embedded views - the id is on screen
         either way, which is what the candidate actually needs. */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title="Copy application ID"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'var(--background)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        color: 'var(--text-secondary)',
      }}
    >
      {value}
      <span style={{ fontSize: 10, fontWeight: 600, color: copied ? 'var(--success, #15803D)' : 'var(--text-muted)' }}>
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  );
}

/**
 * @param {object}   application  application document from /api/applications
 * @param {function} [onViewJob]  called with the populated job, when there is one
 * @param {node}     [footer]     extra content under the card body
 */
export default function ApplicationCard({ application, onViewJob, footer }) {
  if (!application) return null;

  const job = jobOf(application);
  const company = companyOf(job);
  const reference = applicationRef(application);
  const style = APPLICATION_STATUS_STYLE[application.status] || APPLICATION_STATUS_STYLE.Applied;
  const appliedAt = application.applied_at
    ? new Date(application.applied_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px',
      background: 'var(--surface)', display: 'flex', gap: 14, alignItems: 'flex-start',
      flexWrap: 'wrap',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10, background: 'var(--primary-light)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, color: 'var(--primary)', fontSize: 18, flexShrink: 0,
      }}>
        {company[0]?.toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{job?.title || 'Position'}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
          {company}
          {job?.work_mode && <> · {job.work_mode}</>}
          {job?.position_type && <> · {job.position_type}</>}
        </div>
        {appliedAt && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Applied {appliedAt}</div>
        )}
        {reference && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-muted)' }}>
              Application ID
            </span>
            <CopyableRef value={reference} />
          </div>
        )}
        {footer}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <span style={{ ...style, fontSize: 12, padding: '4px 12px', borderRadius: 20, fontWeight: 700 }}>
          {application.status}
        </span>
        {job && onViewJob && (
          <button
            type="button"
            onClick={() => onViewJob(job)}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 12, cursor: 'pointer', fontWeight: 600, padding: 0 }}
          >
            View job →
          </button>
        )}
      </div>
    </div>
  );
}
